import React, { ReactElement, useMemo, useState } from "react";
import { Col, Container, Row, Table, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Chart from "react-apexcharts";
import { StatsService } from "../stats";
import { UserService, IUserTableData } from "../users";
import { Loading } from "../ui";

interface PipelineStage {
	stage: string;
	count: number;
	value: number;
}

interface PipelineData {
	pipeline: PipelineStage[];
	totalOpenValue: number;
}

export const PipelineFunnelPage = (): ReactElement => {
	const [date, setDate] = useState<Date | null>(null);
	const [showAllTime, setShowAllTime] = useState(true);
	const [userId, setUserId] = useState<number | undefined>(undefined);
	const [users, setUsers] = useState<IUserTableData[]>([]);
	const [data, setData] = useState<PipelineData | null>(null);
	const [loading, setLoading] = useState(true);

	const statsService = new StatsService();
	const userService = new UserService();

	const fetchUsers = async () => {
		const userList = await userService.getUsersForTable(true);
		setUsers(userList.filter(u => u.include_in_dashboard_stats));
	};

	const fetchData = async () => {
		setLoading(true);
		const dateToUse = showAllTime ? undefined : (date ?? undefined);
		const result = await statsService.findPipelineFunnel(dateToUse, userId);
		setData(result as PipelineData);
		setLoading(false);
	};

	useMemo(() => {
		fetchUsers();
	}, []);

	useMemo(() => {
		fetchData();
	}, [date, userId, showAllTime]);

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	return (
		<Container fluid className="dashboard-container">
			<div className="dashboard-header-card">
				<div className="dashboard-header-main">
					<div className="dashboard-icon-wrapper">
						<i className="fa fa-filter"></i>
					</div>
					<div className="dashboard-info">
						<h1 className="dashboard-title">Sales Pipeline / Funnel</h1>
						<p className="dashboard-subtitle">
							Visual breakdown of open deals at each sales stage
						</p>
					</div>
				</div>
				<div className="dashboard-header-actions">
					<Form.Select
						className="me-2"
						style={{ width: "200px", display: "inline-block" }}
						value={userId || ""}
						onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : undefined)}
					>
						<option value="">All Sales Reps</option>
						{users.map((user) => (
							<option key={user.id} value={user.id}>
								{user.full_name}
							</option>
						))}
					</Form.Select>
					<Form.Select
						className="me-2"
						style={{ width: "150px", display: "inline-block" }}
						value={showAllTime ? "all" : "month"}
						onChange={(e) => {
							const isAll = e.target.value === "all";
							setShowAllTime(isAll);
							if (!isAll && !date) {
								setDate(new Date());
							}
						}}
					>
						<option value="all">All Time</option>
						<option value="month">Specific Month</option>
					</Form.Select>
					{!showAllTime && (
						<div className="date-picker-wrapper">
							<i className="fa fa-calendar"></i>
							<DatePicker
								className="form-control date-picker-input"
								dateFormat="MM/yyyy"
								selected={date}
								showMonthYearPicker
								onChange={(_date: Date | null) => setDate(_date)}
							/>
						</div>
					)}
				</div>
			</div>

			<div className="dashboard-card mb-4">
				<div className="card-header">
					<i className="fa fa-info-circle"></i>
					<span>About This Report</span>
				</div>
				<div className="card-body">
					<p><strong>What this report shows:</strong> A visual breakdown of all open opportunities organized by their current sales stage. This helps you understand where deals are in your pipeline and identify potential bottlenecks.</p>
					<p><strong>How data is calculated:</strong></p>
					<ul>
						<li><strong>Deal Count:</strong> The number of opportunities currently at each stage (First Contact → Qualifying → Pitch → Negotiation → Signed Contract → Online Order).</li>
						<li><strong>Total Value:</strong> The sum of opportunity amounts at each stage.</li>
						<li><strong>Total Open Pipeline:</strong> The sum of all opportunity amounts across all open stages.</li>
					</ul>
					<p><strong>How to use:</strong> By default, this shows all open opportunities ("All Time"). Select "Specific Month" to filter by opportunities created during a particular month - useful for tracking how a specific cohort is progressing. Filter by sales rep to review individual performance.</p>
				</div>
			</div>

			{loading ? (
				<div className="text-center p-5">
					<Loading /> Loading pipeline data...
				</div>
			) : data ? (
				<>
					<Row className="mb-4">
						<Col lg={4}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-dollar"></i>
									<span>Total Open Pipeline Value</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-primary" style={{ fontSize: "2.5rem" }}>
										{formatCurrency(data.totalOpenValue)}
									</h2>
								</div>
							</div>
						</Col>
						<Col lg={8}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-bar-chart"></i>
									<span>Pipeline by Stage</span>
								</div>
								<div className="card-body">
									<Chart
										options={{
											xaxis: {
												categories: data.pipeline.map((p) => p.stage),
											},
											plotOptions: {
												bar: {
													borderRadius: 4,
													horizontal: true,
												},
											},
											colors: ["#4361ee"],
											dataLabels: {
												enabled: true,
												formatter: (val: number) => val.toString(),
											},
										}}
										series={[
											{
												name: "Deals",
												data: data.pipeline.map((p) => p.count),
											},
										]}
										type="bar"
										height={300}
									/>
								</div>
							</div>
						</Col>
					</Row>

					<Row>
						<Col lg={12}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-table"></i>
									<span>Pipeline Summary</span>
								</div>
								<div className="card-body">
									<Table striped hover responsive>
										<thead>
											<tr>
												<th>Stage</th>
												<th className="text-end">Deal Count</th>
												<th className="text-end">Total Value</th>
											</tr>
										</thead>
										<tbody>
											{data.pipeline.map((stage) => (
												<tr key={stage.stage}>
													<td>{stage.stage}</td>
													<td className="text-end">{stage.count}</td>
													<td className="text-end">{formatCurrency(stage.value)}</td>
												</tr>
											))}
										</tbody>
										<tfoot>
											<tr className="table-primary">
												<td><strong>Total</strong></td>
												<td className="text-end">
													<strong>{data.pipeline.reduce((sum, s) => sum + s.count, 0)}</strong>
												</td>
												<td className="text-end">
													<strong>{formatCurrency(data.totalOpenValue)}</strong>
												</td>
											</tr>
										</tfoot>
									</Table>
								</div>
							</div>
						</Col>
					</Row>
				</>
			) : (
				<div className="text-center p-5">No data available</div>
			)}
		</Container>
	);
};
